import React from 'react';

import type { Currency } from '@models/next/Money';
import { UseQueryResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import minMax from 'dayjs/plugin/minMax';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import * as t from 'io-ts';
import * as E from 'io-ts/Encoder';
import { parse, stringify } from 'query-string';
import { Validator } from 'tyrann-io';

dayjs.extend(customParseFormat);
dayjs.extend(minMax);
dayjs.extend(utc);
dayjs.extend(timezone);

export const id = <T = unknown>(x: T) => x;

export const eq = <T = unknown>(a: T, b: T) => a === b;

export type ID<T> = T;

export type OptionalToUndefined<T> = {
  [K in keyof Required<T>]-?: undefined extends T[K] ? T[K] | undefined : T[K];
};

/**
 *
 * @param v
 * @returns maps optional fields to required T | undefined.
 */
export const optionalToUndefined = <T>(v: T): OptionalToUndefined<T> => {
  return v as any;
};

export const nanToUndefined = (n: number): number | undefined => {
  if (Number.isNaN(n)) return undefined;
  return n;
};

export const formatDateTime = (d?: string) => {
  return dayjs(d).format('YYYY/MM/DD HH:mm');
};

export const formatDate = <T extends string | undefined | null | dayjs.Dayjs>(
  d: T,
  variant?: 'short',
): string | undefined | null => {
  if (!d) return d;
  return dayjs(d).format(variant === 'short' ? 'YY/MM/DD' : 'YYYY/MM/DD');
};

export const toJST = (d: Date) => {
  return `${d.toLocaleString('ja-jp', { timeZone: 'Asia/Tokyo' })} JST`;
};

export const toCST = (d: Date) => {
  return `${d.toLocaleString('ja-jp', { timeZone: 'Asia/Shanghai' })} CST`;
};

export const toJSTDate = (d: Date) => {
  return `${d.toLocaleDateString('ja-jp', { timeZone: 'Asia/Tokyo' })}`;
};

export const toCSTDate = (d: Date) => {
  return `${d.toLocaleDateString('ja-jp', { timeZone: 'Asia/Shanghai' })}`;
};

export const toJSTTime = (d: Date) => {
  return `${d.toLocaleTimeString('ja-jp', { timeZone: 'Asia/Tokyo' })} JST`;
};

export const toCSTTime = (d: Date) => {
  return `${d.toLocaleTimeString('ja-jp', { timeZone: 'Asia/Shanghai' })} CST`;
};

export const formatInputDate = (d: Date, timeZone?: string) => {
  // To: YYYY-MM-DD
  return d
    .toLocaleDateString('zh-Hans-CN', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    .replace(/\//g, '-');
};

export const formatInputTime = (d: Date, timeZone?: string) => {
  // To: HH:MM
  return d.toLocaleTimeString('ja-jp', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const timeZoneToOffsetHours = {
  'Asia/Shanghai': 8,
  'Asia/Tokyo': 9,
} as const;

export const formatISOString = (
  inputDate: string, // YYYY-MM-DD, local date
  inputTime: string, // HH:MM, local time
  timeZone: keyof typeof timeZoneToOffsetHours,
) => {
  const timeZoneOffsetHr = timeZoneToOffsetHours[timeZone] as number;
  const timeZoneSign = timeZoneOffsetHr >= 0 ? '+' : '-';

  return `${inputDate}T${inputTime}:00${timeZoneSign}${String(timeZoneOffsetHr).padStart(
    2,
    '0',
  )}:00`;
};

export const iota = (n: number, start: number = 0) => new Array(n).fill(1).map((_, i) => i + start);

export type Booleanish = 'true' | 'false';

export class FileValidator extends Validator<File> {
  constructor(message?: string) {
    super(
      'File',
      (u): u is File => u instanceof File,
      (v, c) => (v instanceof File ? t.success(v) : t.failure(v, c, message)),
      id,
    );
  }
}

export class BlobValidator extends Validator<Blob> {
  constructor(message?: string) {
    super(
      'Blob',
      (u): u is Blob => u instanceof Blob,
      (v, c) => (v instanceof Blob ? t.success(v) : t.failure(v, c, message)),
      id,
    );
  }
}

export const file = (message?: string) => new FileValidator(message);

export const blob = (message?: string) => new BlobValidator(message);

export const formDataEncoder: E.Encoder<any, {}> = {
  encode: (o) => {
    const formData = new FormData();
    const encodeOne = (key: string, value: any) => {
      formData.append(
        key,
        value instanceof Blob ? value : typeof value === 'string' ? value : JSON.stringify(value),
      );
    };

    for (const [k, v] of Object.entries(o)) {
      if (v !== undefined && v !== null) {
        if (Array.isArray(v)) {
          for (const item of v) {
            encodeOne(k, item);
          }
        } else {
          encodeOne(k, v);
        }
      }
    }
    return formData;
  },
};

export const stringifySearch = <T extends Record<string, unknown>>(object: T) => {
  return stringify(object, {
    arrayFormat: 'bracket-separator',
  });
};

export const parseSearch = (s: string) => {
  return parse(s, {
    arrayFormat: 'bracket-separator',
  });
};

export const mergeStringObject = (o: any): string => {
  return Object.values(o ?? {})
    .map(
      // eslint-disable-next-line no-nested-ternary
      (v) => (typeof v === 'string' ? v : typeof v === 'object' ? mergeStringObject(v) : undefined),
    )
    .filter(Boolean)
    .join('');
};

export const formatNumberWithSeparators = (n: number): string => {
  return Number(n).toLocaleString('en-US');
};

// Maps YYYY/MM/DD to YYYY-MM-DD
export const formatSlashDate = (date: string) => {
  if (date) return date.replace(/\//g, '-');
};

export const formatPrice = (amount: number, type: Currency) => {
  switch (type) {
    case 'USD':
      return amount.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
        style: 'currency',
        currency: 'USD',
      });
    case 'JPY':
      return amount.toLocaleString('ja-JP', {
        maximumFractionDigits: 0,
        style: 'currency',
        currency: 'JPY',
      });
  }
};

export const roundPrice = (amount: number, type: Currency) => {
  if (type === 'JPY') {
    return Math.round(amount);
  } else if (type === 'USD') {
    return Math.round(amount * 100) / 100;
  }
  throw new Error(`Unknown type: ${type}`);
};

export const mergeWithNonNullish = <T extends {}>(merged: T, input: any): T => {
  const output: any = {};

  for (const [key, value] of Object.entries(merged)) {
    output[key] = input[key] ?? value;
  }

  return output;
};

export const DEFAULT_PAGE_SIZE = 50;

export const DEFAULT_CLIENT_PAGE_SIZE = 10;

export const DEFAULT_PAGE_OPTIONS = [1, 10, 50, 100];

export const validateEnglish = (s: string) => !/[一-龯ぁ-んァ-ン！：／]/.test(s);

export const IS_NODE = typeof location === 'undefined';

export const isValidJSON = (s: string) => {
  try {
    JSON.parse(s);
    return true;
  } catch (e) {
    return false;
  }
};

export function isValidDate(d: string) {
  return dayjs(d, 'YYYY-MM-DD', true).isValid();
}

/**
 * @param o The object
 * @returns the first dot-seperate path of that object.
 *          useful for finding the name of the input html element
 *          for the first formik error.
 *          Example: { a: { b: 1 } } returns 'a.b'
 */
export function findFirstPath(o: {}): string | undefined {
  if (typeof o === 'object') {
    for (const [k, v] of Object.entries(o)) {
      const p = findFirstPath(v) || '';
      return k + (p.length > 0 ? '.' + p : '');
    }
    return undefined;
  }
  return '';
}

export const withDefaultProps = <T extends {}, U extends Partial<T>>(
  fc: React.FC<T>,
  defaultProps: U,
): React.FC<Omit<T, keyof U> & Partial<U>> => {
  const wrapper: React.FC<Omit<T, keyof U> & Partial<U>> = (props) => {
    return React.createElement(fc, { ...defaultProps, ...props } as any);
  };

  wrapper.displayName = fc.displayName;
  return wrapper;
};

export const mapOptional = <T, U>(optional: T, f: (t: T & {}) => U): U | undefined => {
  if (optional !== null && optional !== undefined) {
    return f(optional);
  }
  return undefined;
};

export const mapOptionalOr = <T, U>(
  optional: T,
  f: (t: T & {}) => U,
  def: () => U,
): U | undefined => {
  if (optional !== null && optional !== undefined) {
    return f(optional);
  }
  return def();
};

export const toggleArray = <T>(array: T[], element: T, head: boolean = true) => {
  if (array.includes(element)) {
    return array.filter((e) => e !== element);
  } else {
    if (head) {
      return [element, ...array];
    } else {
      return [...array, element];
    }
  }
};

/**
 *
 * @param ordering current ordering array, like ["age", "-name", "-created_at"]
 * @param order the next ordering item
 * @returns the new ordering with `order` at the head, removing all current orders with the same name as it.
 */
export const addOrdering = (ordering: string[], order: string) => {
  const orderName = order.replace(/^-/, '');

  return [order, ...ordering.filter((order) => !order.endsWith(orderName))];
};

export const includes = <T extends U, U>(array: ReadonlyArray<T>, el: U): el is T =>
  array.includes(el as T);

/**
 *
 * @param u source object
 * @param v compared object.
 * @returns the properties in v that are not equal to u's counterpart
 */
export const diffPartial = <U extends {}, V extends {}>(u: U, v: V): Partial<V> => {
  const r: Record<string, any> = {};

  for (const [key, value] of Object.entries(v)) {
    if (!Object.is(value, (u as any)[key])) {
      r[key] = value;
    }
  }

  return r as any;
};

export const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const counter = (start = 1) => {
  return () => start++;
};

export type MappedQueriesData<Queries extends Record<string, UseQueryResult<any, any>>> = {
  [K in keyof Queries]: Queries[K]['data'] & {};
};

/**
 *
 * @param queries
 * @param map
 * @returns Given a map of queries, map them to their success data. If one of them have nullish result, return undefined.
 * This reduces the usage of `?` or `query.data === undefined` when you have multiple queries to handle their status.
 */
export const mapQueriesData = <Queries extends Record<string, UseQueryResult<any, any>>, R>(
  queries: Queries,
  map: (data: MappedQueriesData<Queries>) => R,
): R | undefined => {
  for (const q of Object.values(queries)) {
    if (q.data == null) return undefined;
  }

  return map(Object.fromEntries(Object.entries(queries).map(([k, v]) => [k, v.data])) as any);
};

export function cloneRegexWithSource(input: RegExp, newSource: string) {
  return new RegExp(newSource, input.flags);
}

export function regexOrEmpty(input: RegExp) {
  return cloneRegexWithSource(input, input.source + '|' + '^$');
}

export type RecordToKV<O extends Record<string, any>> = [keyof O, O[keyof O]][];

export const recordToKV = <O extends Record<string, any>>(o: O): RecordToKV<O> => {
  return Object.entries(o);
};

export const isImageFileName = (file: string) => file.match(/\.(jpe?g|png|svg|webp|gif|bmp)$/i);

export const isPdfFileName = (file: string) => file.match(/\.pdf$/i);

export const plural = (count: number, unit: string, pluralUnit?: string) =>
  count <= 1 ? unit : pluralUnit ?? `${unit}s`;
